// Modal de vínculo de número WhatsApp desconhecido a cliente existente (Story 5.11).
// Trigger: botão "Vincular a cliente" no card whatsapp_from_unknown da página de divergências.

import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Check, Search, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { LinkClientTarget } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Input, Select } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { fetchClients, type ClientEntity } from '../../lib/clients-api';
import { linkAlertToClient } from '../../lib/dashboard-api';
import { extractErrorMessage } from '../../lib/errors';

const TARGET_OPTIONS: { value: LinkClientTarget; label: string }[] = [
  { value: 'whatsapp', label: 'WhatsApp principal do cliente' },
  { value: 'financial_whatsapp', label: 'WhatsApp financeiro do cliente' },
  { value: 'contact', label: 'Novo contato do cliente (sócio, atendente…)' },
];

interface LinkClientModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alert: { id: string; fromNumber: string; suggestedName: string | null } | null;
  onLinked: () => void;
}

function isPhoneFieldOccupied(err: unknown): boolean {
  return (
    axios.isAxiosError<{ error?: { code?: string } }>(err) &&
    err.response?.data?.error?.code === 'PHONE_FIELD_OCCUPIED'
  );
}

export function LinkClientModal({
  open,
  onOpenChange,
  alert,
  onLinked,
}: LinkClientModalProps): JSX.Element {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ClientEntity | null>(null);
  const [target, setTarget] = useState<LinkClientTarget>('whatsapp');
  const [contactName, setContactName] = useState('');

  const results = useQuery({
    queryKey: ['link-client-search', search],
    queryFn: () => fetchClients({ q: search, limit: 8 }),
    enabled: open && search.length >= 2,
    staleTime: 30_000,
  });

  const linkM = useMutation({
    mutationFn: (input: { clientId: string; target: LinkClientTarget; contactName?: string }) =>
      linkAlertToClient(alert?.id ?? '', input),
    onSuccess: ({ data }) => {
      toast.success(
        `Número vinculado — ${data.resolvedCount} divergência(s) resolvida(s) e mensagem registrada no histórico.`,
      );
      handleOpenChange(false);
      onLinked();
    },
    onError: (err) => {
      if (isPhoneFieldOccupied(err)) {
        setTarget('contact');
        toast.error(
          'Esse campo do cliente já tem outro número. Mudei a opção pra "Novo contato" — confirme o nome e tente de novo.',
        );
        return;
      }
      toast.error(extractErrorMessage(err, 'Não conseguimos vincular o número.'));
    },
  });

  function handleOpenChange(next: boolean): void {
    if (!next) {
      setSearchInput('');
      setSearch('');
      setSelected(null);
      setTarget('whatsapp');
      setContactName('');
    }
    onOpenChange(next);
  }

  function handleSubmit(): void {
    if (!alert || !selected) return;
    if (target === 'contact' && contactName.trim().length === 0) {
      toast.error('Informe o nome do contato.');
      return;
    }
    linkM.mutate({
      clientId: selected.id,
      target,
      contactName: target === 'contact' ? contactName.trim() : undefined,
    });
  }

  const items = results.data?.items ?? [];

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(560px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[12px] border border-line bg-panel p-6 shadow-2xl',
          )}
        >
          <div className="mb-5 flex items-center justify-between">
            <div>
              <Dialog.Title className="text-[16px] font-semibold tracking-[-0.005em]">
                Vincular número a cliente
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-[12px] text-muted tnum">
                {alert?.fromNumber ?? ''} — busque o cliente pelo nome.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-[8px] p-1.5 text-muted hover:bg-panel-2 hover:text-ink"
              >
                <X size={16} />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex flex-col gap-4">
            <Field label="Buscar cliente" hint="Nome, e-mail ou documento — mínimo 2 caracteres">
              {({ inputId }) => (
                <div className="flex gap-1">
                  <Input
                    id={inputId}
                    placeholder="Ex: Maria, Clínica Bella…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput.trim())}
                  />
                  <PillButton
                    icon={<Search size={14} strokeWidth={2} />}
                    onClick={() => setSearch(searchInput.trim())}
                    disabled={results.isFetching || searchInput.trim().length < 2}
                  >
                    Buscar
                  </PillButton>
                </div>
              )}
            </Field>

            {results.isFetching && <p className="m-0 text-[12px] text-muted">Buscando…</p>}

            {!results.isFetching && search.length >= 2 && items.length === 0 && (
              <p className="m-0 text-[12px] text-muted">
                Nenhum cliente encontrado pra &ldquo;{search}&rdquo;.
              </p>
            )}

            {items.length > 0 && (
              <ul className="m-0 max-h-[200px] list-none divide-y divide-line overflow-y-auto rounded-[10px] border border-line p-0">
                {items.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(c)}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[13px] hover:bg-panel-2',
                        selected?.id === c.id && 'bg-panel-2',
                      )}
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-ink">{c.name}</span>
                        <span className="block truncate text-[11px] text-muted-2">
                          {c.document ?? c.email ?? 'sem documento'}
                        </span>
                      </span>
                      {selected?.id === c.id && (
                        <Check size={14} strokeWidth={2} className="shrink-0 text-good" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {selected && (
              <>
                <Field label="Onde salvar o número">
                  {({ inputId }) => (
                    <Select
                      id={inputId}
                      value={target}
                      onChange={(e) => {
                        const next = e.target.value as LinkClientTarget;
                        setTarget(next);
                        // AC11 — prefill do nome com o pushName do WhatsApp quando houver
                        if (next === 'contact' && !contactName && alert?.suggestedName) {
                          setContactName(alert.suggestedName);
                        }
                      }}
                    >
                      {TARGET_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>

                {target === 'contact' && (
                  <Field label="Nome do contato" required>
                    {({ inputId }) => (
                      <Input
                        id={inputId}
                        placeholder={alert?.suggestedName ?? 'Ex: João (sócio)'}
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        maxLength={200}
                      />
                    )}
                  </Field>
                )}
              </>
            )}

            <div className="mt-2 flex items-center justify-end gap-2">
              <PillButton type="button" onClick={() => handleOpenChange(false)}>
                Cancelar
              </PillButton>
              <PillButton
                type="button"
                solid
                onClick={handleSubmit}
                disabled={!selected || linkM.isPending}
              >
                {linkM.isPending ? 'Vinculando…' : 'Vincular número'}
              </PillButton>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
